import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { CreateSalesDealActuals, UpdateSalesDealActuals } from '@repo/backend/crt'
import { PRODUCT_CATEGORIES, SALES_DEAL_ACTUALS_CREATE_ERROR_TYPES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const salesDealActualsSearchSchema = z.object({
  keyword: z.string().optional(),
  period: monthStartString.optional(),
  ...datatableSearchSchema.shape,
})

export const salesDealActualsDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  budgetAllocation: z.array(z.string()).optional(),
  conditionType: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  hierarchyErp: z.array(z.string()).optional(),
  masterAgreement: z.array(z.string()).optional(),
  agreementNumber: z.array(z.string()).optional(),
  billingDateFrom: z.coerce.date().optional(),
  billingDateTo: z.coerce.date().optional(),
  ...datatableSearchSchema.shape,
})

export type SalesDealActualsSearch = z.infer<typeof salesDealActualsSearchSchema>
export type SalesDealActualsDetailsSearch = z.infer<typeof salesDealActualsDetailsSearchSchema>

export type SalesDealActualsList = Awaited<ReturnType<typeof getSalesDealActualsList>>
export type SalesDealActual = SalesDealActualsList['results'][number]
export type SalesDealActualWithDetails = Awaited<ReturnType<typeof getSalesDealActualById>>

type UseCreateSalesDealActualsArgs = {
  queries: SalesDealActualsSearch
  handleOnSuccess: (salesDealActualId: number) => void
}

type UseUpdateSalesDealActualsArgs = QueryHandlers & {
  salesDealActualId: number
}

type UseDeleteSalesDealActualsArgs = QueryHandlers & {
  salesDealActualIds: number[]
  queries: SalesDealActualsSearch
}

async function getCurrentSalesDealActuals(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const query = crtPeriod ? { crtPeriod } : {}
  const res = await api.crt['sales-deal-actuals'].current.$get({ query }, { init: { signal } })
  return await res.json()
}

export const currentSalesDealActualsQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentSalesDealActuals', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getCurrentSalesDealActuals(crtPeriod, signal),
})

export function useCurrentSalesDealActuals(crtPeriod?: string) {
  return useQuery(currentSalesDealActualsQueryOptions(crtPeriod))
}

/**
 * The import holding one period — its month of billing — or `null`. Detail pages are addressed by that
 * period, so their loader resolves the import's id from it through the list endpoint's period filter.
 */
export const salesDealActualByPeriodQueryOptions = (period: string) => ({
  queryKey: ['salesDealActualByPeriod', period] as const,
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const { results } = await getSalesDealActualsList({ period, page: 1, pageSize: 1 }, signal)
    return results[0] ?? null
  },
})

async function getLatestSalesDealActuals(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['sales-deal-actuals'].latest.$get({}, { init: { signal } })
  return await res.json()
}

export function useLatestSalesDealActuals() {
  return useQuery({
    queryKey: ['latestSalesDealActuals'] as const,
    queryFn: ({ signal }) => getLatestSalesDealActuals(signal),
  })
}

async function getSalesDealActualsList(search: SalesDealActualsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['sales-deal-actuals'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getSalesDealActualById(
  id: string,
  queries: SalesDealActualsDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const { billingDateFrom, billingDateTo, ...rest } = queries
  const res = await api.crt['sales-deal-actuals'][':id'].$get(
    {
      param: { id },
      query: stringifyValues({
        ...rest,
        billingDateFrom: billingDateFrom ? formatDateOnly(billingDateFrom) : undefined,
        billingDateTo: billingDateTo ? formatDateOnly(billingDateTo) : undefined,
      }),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function createSalesDealActuals(payload: CreateSalesDealActuals) {
  const api = getApi()

  const res = await api.crt['sales-deal-actuals'].$post({
    json: payload,
  })

  return await res.json()
}

async function updateSalesDealActuals(salesDealActualId: number, details: UpdateSalesDealActuals) {
  const api = getApi()

  await api.crt['sales-deal-actuals'][':id'].$patch({
    param: { id: String(salesDealActualId) },
    json: details,
  })
}

async function deleteSalesDealActuals(salesDealActualIds: number[]) {
  const api = getApi()

  await api.crt['sales-deal-actuals'].bulk.$delete({
    json: { ids: salesDealActualIds },
  })
}

export function useSalesDealActualsList(queries: SalesDealActualsSearch) {
  return useQuery({
    queryKey: ['salesDealActuals', queries],
    queryFn: ({ signal }) => getSalesDealActualsList(queries, signal),
  })
}

export const salesDealActualByIdQueryOptions = (
  id: string,
  queries: SalesDealActualsDetailsSearch = {},
) => ({
  queryKey: ['salesDealActualById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getSalesDealActualById(id, queries, signal),
})

export function useSalesDealActualById(id: string, queries: SalesDealActualsDetailsSearch = {}) {
  return useQuery(salesDealActualByIdQueryOptions(id, queries))
}

async function getAgreementNumbers(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['sales-deal-actuals'][':id']['agreement-numbers'].$get(
    {
      param: { id },
    },
    { init: { signal } },
  )

  return await res.json()
}

export function useSalesDealActualAgreementNumbers(id: string) {
  return useQuery({
    queryKey: ['salesDealActualAgreementNumbers', id],
    queryFn: ({ signal }) => getAgreementNumbers(id, signal),
  })
}

export type CreatedSalesDealActualExportJob = Awaited<
  ReturnType<typeof createSalesDealActualDetailsExportJob>
>

type UseCreateSalesDealActualExportJobArgs = {
  salesDealActualId: string
  onJobCreated: (job: CreatedSalesDealActualExportJob) => void
}

async function createSalesDealActualDetailsExportJob(
  id: string,
  queries: SalesDealActualsDetailsSearch = {},
) {
  const api = getApi()
  const { billingDateFrom, billingDateTo, ...rest } = queries
  const res = await api.crt['sales-deal-actuals'][':id']['export-jobs'].$post({
    param: { id },
    query: stringifyValues({
      ...rest,
      billingDateFrom: billingDateFrom ? formatDateOnly(billingDateFrom) : undefined,
      billingDateTo: billingDateTo ? formatDateOnly(billingDateTo) : undefined,
    }),
  })

  return await res.json()
}

async function createSalesDealActualRawExportJob(id: string) {
  const api = getApi()
  const res = await api.crt['sales-deal-actuals'][':id']['raw-export-jobs'].$post({ param: { id } })
  return await res.json()
}

export function useCreateSalesDealActualDetailsExportJob({
  salesDealActualId,
  onJobCreated,
}: UseCreateSalesDealActualExportJobArgs) {
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createSalesDealActualDetailsExportJob', salesDealActualId],
    mutationFn: (queries: SalesDealActualsDetailsSearch) =>
      createSalesDealActualDetailsExportJob(salesDealActualId, queries),
    onSuccess: (job) => {
      onJobCreated(job)
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('export.startFailed', { ns: 'common' }),
        className: toastClassName,
      })
    },
  })
}

export function useCreateSalesDealActualRawExportJob({
  salesDealActualId,
  onJobCreated,
}: UseCreateSalesDealActualExportJobArgs) {
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createSalesDealActualRawExportJob', salesDealActualId],
    mutationFn: () => createSalesDealActualRawExportJob(salesDealActualId),
    onSuccess: (job) => {
      onJobCreated(job)
    },
    onError: () => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: t('export.startFailed', { ns: 'common' }),
        className: toastClassName,
      })
    },
  })
}

export function useCreateSalesDealActuals({
  queries,
  handleOnSuccess,
}: UseCreateSalesDealActualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['salesDealActuals', queries],
    mutationFn: createSalesDealActuals,
    onSuccess: (salesDealActual) => {
      handleOnSuccess(salesDealActual.id)

      queryClient.invalidateQueries({
        queryKey: ['salesDealActuals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('salesDealActuals.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; period?: string }
        | undefined

      let message: string
      switch (customParams?.type) {
        case SALES_DEAL_ACTUALS_CREATE_ERROR_TYPES.periodConflict:
          message = `${t('errors.salesDealActuals.createFailed', { ns: 'crt' })}: ${t(
            'errors.salesDealActuals.periodConflict',
            { ns: 'crt', period: customParams.period },
          )}`
          break
        default:
          message = getFirstZodErrorMessage(
            issues,
            t,
            'errors.salesDealActuals.createFailed',
            'crt',
          )
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateSalesDealActuals({
  salesDealActualId,
  handleOnSuccess,
}: UseUpdateSalesDealActualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateSalesDealActuals', salesDealActualId],
    mutationFn: (details: UpdateSalesDealActuals) =>
      updateSalesDealActuals(salesDealActualId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['salesDealActualById', String(salesDealActualId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('salesDealActuals.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.salesDealActuals.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteSalesDealActuals({
  salesDealActualIds,
  queries,
  handleOnSuccess,
}: UseDeleteSalesDealActualsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteSalesDealActuals'],
    mutationFn: () => deleteSalesDealActuals(salesDealActualIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['salesDealActuals', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('salesDealActuals.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.salesDealActuals.deleteFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
